import React, { useState, useEffect } from 'react';
import { X, User, Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { apiService } from '../../services/api';

interface ClientEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onSuccess: () => void;
}

const ClientEditModal: React.FC<ClientEditModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    whatsapp: '',
    reseller_notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        username: user.username || '',
        password: user.password || '',
        email: user.email || '',
        whatsapp: user.whatsapp || '',
        reseller_notes: user.reseller_notes || ''
      });
    }
  }, [isOpen, user]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!formData.username) {
      alert('Nome de usuário é obrigatório');
      return;
    }
    if (formData.username.trim().length < 8) {
      alert('Username deve ter no mínimo 8 caracteres (regra do painel XUI)');
      return;
    }
    if (formData.password && formData.password.trim().length > 0 && formData.password.trim().length < 8) {
      alert('Senha deve ter no mínimo 8 caracteres (regra do painel XUI)');
      return;
    }

    setLoading(true);
    try {
      const updateData: Record<string, string> = {
        username: formData.username.trim(),
        email: formData.email,
        whatsapp: formData.whatsapp,
        reseller_notes: formData.reseller_notes
      };
      // Só envia senha se o usuário digitou uma nova (vazio = manter)
      if (formData.password && formData.password.trim()) {
        updateData.password = formData.password.trim();
      }

      await apiService.updateUser(user.id, updateData);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro ao atualizar usuário:', error);
      
      if (error?.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else if (error?.response?.status === 403) {
        alert('Acesso negado. Você não tem permissão para realizar esta ação.');
      } else {
        const details = error?.response?.data?.details;
        const msg =
          (typeof details === 'string' ? details : null) ||
          details?.[0]?.message ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Erro ao atualizar usuário';
        alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <User className="w-5 h-5" />
            Editar Cliente
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nome de Usuário *</Label>
              <Input
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="Nome de usuário"
              />
            </div>

            <div>
              <Label>Senha</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Nova senha (deixe vazio para manter)"
              />
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="email@exemplo.com"
              />
            </div>

            <div>
              <Label>WhatsApp</Label>
              <Input
                value={formData.whatsapp}
                onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                placeholder="5511999999999"
              />
            </div>
          </div>

          <div>
            <Label>Observações do Revendedor</Label>
            <Textarea
              value={formData.reseller_notes}
              onChange={(e) => handleInputChange('reseller_notes', e.target.value)}
              placeholder="Observações sobre o cliente..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || !formData.username}
              className="flex items-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Salvar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientEditModal;